import { useLazyQuery } from '@apollo/client';
import type {
  FetchPhysicalAddressQuery,
  FetchPhysicalAddressQueryVariables,
  SearchPhysicalAddressQuery,
  SearchPhysicalAddressQueryVariables,
} from '@generated/graphql';
import type { PhysicalAddress } from '@interfaces/physicalAddress';
import LockIcon from '@mui/icons-material/Lock';
import { InputAdornment, type SxProps } from '@mui/material';
import type { CountryCodes, Jurisdictions } from '@onevesthq/ov-enums';
import { colors } from '@ovComponents/0-tokens/ref/palette/palette';
import { Icon } from '@ovComponents/1-primative/icon/icon';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { getCountryName } from '@ovComponents/resources';
import { useGlobalToast } from '@providers/globalToastProvider';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { kebabCase, throttle } from 'lodash-es';
import {
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { JSX } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from '../autocomplete/autocomplete';
import { Tooltip } from '../tooltip/tooltip';
import {
  FETCH_PHYSICAL_ADDRESS,
  SEARCH_PHYSICAL_ADDRESS,
} from './addressField.queries';
import { dedupeAddressOptionsByIdOrText } from './addressOption.utils';
import { extractPostalCodeFromText, isCompletePostalCode } from './postalCode';

type Props = {
  label?: string;
  addressString?: string;
  setAddress: (address: Partial<PhysicalAddress>) => void;
  openManualInput: () => void;
  integrationId?: string;
  organizationId?: string;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  autoFocus?: any;
  onFocus?: any;
  onBlur?: any;
  sx?: SxProps;
  infoTooltip?: string;
  addressCountry?: CountryCodes;
  allowedCountries: CountryCodes[];
  lockMessage?: string;
  testId?: string;
  onLookupFault?: () => void;
};

type PlaceType = {
  text: string;
  place_id?: string;
  id?: string;
};

const GoogleAddress = ({
  setAddress,
  openManualInput,
  label,
  addressString,
  addressCountry,
  error,
  integrationId,
  organizationId,
  errorText,
  disabled,
  onFocus,
  autoFocus,
  sx,
  onBlur,
  infoTooltip,
  allowedCountries,
  onLookupFault,
  lockMessage,
  testId,
}: Props): JSX.Element => {
  const [restrictedCountry, setRestrictedCountry] = useState<CountryCodes>(
    allowedCountries[0],
  );
  const [value, setValue] = useState<PlaceType | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<readonly PlaceType[]>([]);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const { t } = useTranslation(['pageConfiguration', 'components']);
  const { sys } = useThemeTokens();
  const { showToast } = useGlobalToast();
  const inputRef = useRef<any>(null);

  const [searchAddress] = useLazyQuery<
    SearchPhysicalAddressQuery,
    SearchPhysicalAddressQueryVariables
  >(SEARCH_PHYSICAL_ADDRESS);
  const [fetchAddress] = useLazyQuery<
    FetchPhysicalAddressQuery,
    FetchPhysicalAddressQueryVariables
  >(FETCH_PHYSICAL_ADDRESS);

  useEffect(() => {
    if (addressCountry && allowedCountries.includes(addressCountry)) {
      setRestrictedCountry(addressCountry);
    }
  }, [addressCountry, allowedCountries]);

  const handleLookupError = useCallback(
    (callback: (results?: readonly PlaceType[]) => void) => {
      showToast({
        message:
          'Failed to fetch address suggestions. Fallback to manual input.',
        severity: 'error',
      });
      if (onLookupFault) onLookupFault();
      callback([]);
    },
    [showToast, onLookupFault],
  );

  const fetch = useMemo(
    () =>
      throttle(
        async (
          request: {
            input: string;
            countryCode: CountryCodes;
          },
          callback: (results?: readonly PlaceType[]) => void,
        ) => {
          if (!integrationId || !organizationId) {
            handleLookupError(callback);
            return;
          }

          try {
            const { data } = await searchAddress({
              variables: {
                input: {
                  integrationId,
                  organizationId,
                  searchTerm: request.input,
                  countryCode: request.countryCode,
                },
              },
            });

            if (!data?.searchPhysicalAddress?.suggestions) {
              handleLookupError(callback);
              return;
            }

            const suggestions = data.searchPhysicalAddress.suggestions;
            const formattedSuggestions = suggestions.map((s) => ({
              text: s.text,
              place_id: s.id,
              id: s.id,
            }));
            callback(dedupeAddressOptionsByIdOrText(formattedSuggestions));
          } catch {
            handleLookupError(callback);
          }
        },
        200,
      ),
    [integrationId, organizationId, searchAddress, handleLookupError],
  );

  useEffect(() => {
    let active = true;

    if (!inputValue) {
      setOptions(value ? [value] : []);
      return undefined;
    }

    if (!hasBeenFocused) return undefined;

    fetch(
      {
        input: inputValue,
        countryCode: restrictedCountry,
      },
      (results?: readonly PlaceType[]) => {
        if (active) {
          let newOptions: readonly PlaceType[] = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(dedupeAddressOptionsByIdOrText(newOptions));
        }
      },
    );
    return () => {
      active = false;
    };
  }, [value, inputValue, fetch, restrictedCountry, hasBeenFocused]);

  if (!allowedCountries || allowedCountries.length === 0) return <></>;

  const handleSelect = async (
    placeId: string,
    selectedText: string,
  ): Promise<void> => {
    if (!integrationId || !organizationId) return;

    try {
      const { data } = await fetchAddress({
        variables: {
          input: {
            integrationId,
            organizationId,
            addressId: placeId,
          },
        },
      });

      const result = data?.fetchPhysicalAddress?.addressDetails;
      if (result) {
        const selectedPostalCode = extractPostalCodeFromText(selectedText);
        const normalizedPostalCode = isCompletePostalCode(
          result.postal ?? undefined,
        )
          ? (result.postal ?? undefined)
          : selectedPostalCode;

        const localAddress: Partial<PhysicalAddress> = {
          houseNumber: result.houseNumber ?? undefined,
          unitNumber: result.unitNumber ?? undefined,
          streetName: result.streetName ?? undefined,
          city: result.city ?? undefined,
          postal: normalizedPostalCode || undefined,
          jurisdiction: result.jurisdiction as Jurisdictions | undefined,
          country: result.country as CountryCodes | undefined,
          neighborhood: result.neighborhood ?? undefined,
        };
        setAddress(localAddress);
        openManualInput();
      }
    } catch {
      showToast({
        message: 'Failed to fetch address details. Fallback to manual input.',
        severity: 'error',
      });
    }
  };

  return (
    <>
      <Typography
        variant='labelSmall'
        sx={{
          mb: '4px',
          color: error ? colors.negative : sys.color.onSurfaceVariant,
        }}
      >
        {label}
      </Typography>
      {allowedCountries.length > 1 && (
        <RadioGroup
          testId={
            testId ? `${testId}-radio-group` : 'google-address-radio-group'
          }
          value={restrictedCountry}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setRestrictedCountry(e.target.value as CountryCodes);
            setValue(null);
            setInputValue('');
            setAddress({});
          }}
        >
          {allowedCountries.map((cc) => (
            <Radio
              disabled={disabled}
              key={cc}
              value={cc}
              size='small'
              label={getCountryName(cc)}
              testId={
                testId
                  ? `${testId}-radio-option-${cc.toLowerCase()}`
                  : `google-address-radio-option-${cc}`
              }
            />
          ))}
        </RadioGroup>
      )}
      <Autocomplete
        testId={testId ?? 'google-address-autocomplete'}
        label=''
        infoTooltip={infoTooltip}
        error={error}
        errorText={
          errorText ?? t('components:fieldErrors.default', { fieldName: label })
        }
        disabled={disabled}
        onFocus={() => {
          setHasBeenFocused(true);
          if (onFocus) onFocus();
        }}
        autoFocus={autoFocus}
        blurOnSelect
        fullWidth
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.text
        }
        freeSolo
        onBlur={onBlur}
        sx={sx}
        InputProps={{
          inputRef,
          endAdornment: disabled ? (
            <InputAdornment position='end'>
              <Tooltip title={lockMessage ?? t('notEditableMessage')}>
                <Icon icon={LockIcon} size='small' />
              </Tooltip>
            </InputAdornment>
          ) : null,
          disabled,
        }}
        options={[...options]}
        value={addressString}
        renderOption={(props: any, option: PlaceType) => (
          <li
            {...props}
            data-testid={
              testId
                ? `${testId}-${kebabCase(option.text)}`
                : `google-address-autocomplete-${kebabCase(option.text)}`
            }
          >
            {option.text}
          </li>
        )}
        onChange={(
          _: React.ChangeEvent<HTMLInputElement>,
          newValue: PlaceType | null,
        ) => {
          setOptions((currentOptions) =>
            dedupeAddressOptionsByIdOrText(
              newValue ? [newValue, ...currentOptions] : currentOptions,
            ),
          );
          setValue(newValue);
          if (newValue) {
            const addressId = newValue.place_id || newValue.id;
            if (addressId) handleSelect(addressId, newValue.text);
          }
        }}
        onInputChange={(
          _: React.ChangeEvent<HTMLInputElement>,
          newInputValue: SetStateAction<string>,
        ) => {
          setInputValue(newInputValue);
        }}
        filterOptions={(x) => x}
      />
    </>
  );
};

export default GoogleAddress;
